import { useEffect, useMemo, useState } from 'react'

import { ExperimentDataParams } from '../../experiments/hooks/useSendExperimentData'
import { ExperimentUUIDs } from '../../experiments/types'
import { getMemberRecommendedBadgeData } from '../utils'

interface useShouldShowMemberRecommendedBadgeArgs {
  showMemberRecommendedBadgeFlag?: boolean
  isProviderRecommended?: boolean
  isTopProviderInResultsList?: boolean
  isScreenEligibleToDisplayBadge?: boolean
  updateLDContext?: (data: any) => Promise<void>
  sendExperimentData?: ({ experimentUUID, joinKeys }: ExperimentDataParams) => void
  isFocused?: boolean
  // PROVREC-5360: Temporary logging to help debug why showMemberRecommendedBadgeFlag is not consistent between page views
  onLogBeforeExperimentRequest?: () => void
  onLogAfterExperimentRequest?: ({
    shouldShowMemberRecommendedBadge,
    isEligibleForMemberRecommendedBadge,
  }: {
    shouldShowMemberRecommendedBadge: boolean
    isEligibleForMemberRecommendedBadge: boolean
  }) => void
}

/**
 * Hook to encapsulate logic for when the member-recommended badge should be displayed
 *
 * @param showMemberRecommendedBadgeFlag - The value of the LaunchDarkly flag
 * @param isProviderRecommended - Value of whether the provider is recommended from the backend
 * @param isTopProviderInResultsList - Use for provider list views to show badge only on top provider
 * @param isScreenEligibleToDisplayBadge - Use for profile views to show badge on all recommended providers
 * @param updateLDContext - app-specific instance of function to send LaunchDarkly context
 * @param sendExperimentData - app-specific instance of function to send experiment data to the backend
 * @param isFocused - mobile-specific parameter to know when the screen is focused
 * @param onLogBeforeExperimentRequest - app-specific callback to send SumoLogic logs
 * @param onLogAfterExperimentRequest - app-specific callback to send SumoLogic logs
 */
export const useShouldShowMemberRecommendedBadge = ({
  showMemberRecommendedBadgeFlag = false,
  isProviderRecommended,
  isTopProviderInResultsList = false,
  isScreenEligibleToDisplayBadge = false,
  updateLDContext,
  sendExperimentData,
  isFocused,
  // PROVREC-5360: Temporary logging to help debug why showMemberRecommendedBadgeFlag is not consistent between page views
  onLogBeforeExperimentRequest,
  onLogAfterExperimentRequest,
}: useShouldShowMemberRecommendedBadgeArgs) => {
  const [isLDContextUpdated, setIsLDContextUpdated] = useState(false)
  const [hasSentExperimentData, setHasSentExperimentData] = useState(false)

  const data = useMemo(
    () =>
      getMemberRecommendedBadgeData({
        showMemberRecommendedBadgeFlag,
        isProviderRecommended,
        isTopProviderInResultsList,
        isScreenEligibleToDisplayBadge,
      }),
    [isProviderRecommended, isScreenEligibleToDisplayBadge, isTopProviderInResultsList, showMemberRecommendedBadgeFlag],
  )

  useEffect(() => {
    if (
      !isFocused ||
      isLDContextUpdated ||
      isProviderRecommended == null ||
      !updateLDContext ||
      showMemberRecommendedBadgeFlag == null
    )
      return

    const onUpdateLDContext = async () => {
      await updateLDContext({ memberRecommendedProviderShown: isProviderRecommended })
      setIsLDContextUpdated(true)
    }

    onUpdateLDContext()
  }, [isFocused, isLDContextUpdated, isProviderRecommended, showMemberRecommendedBadgeFlag, updateLDContext])

  useEffect(() => {
    const sendClientTestimonialData = async () => {
      if (!sendExperimentData || !isLDContextUpdated || !isFocused || hasSentExperimentData) return
      if (isProviderRecommended == null || showMemberRecommendedBadgeFlag == null) {
        return
      }
      try {
        onLogBeforeExperimentRequest?.()
      } catch {}

      sendExperimentData({ experimentUUID: ExperimentUUIDs.ClientTestimonialV1UUID })
      setHasSentExperimentData(true)

      try {
        onLogAfterExperimentRequest?.(data)
      } catch {}
    }
    sendClientTestimonialData()
  }, [
    data,
    hasSentExperimentData,
    isFocused,
    isLDContextUpdated,
    isProviderRecommended,
    onLogAfterExperimentRequest,
    onLogBeforeExperimentRequest,
    sendExperimentData,
    showMemberRecommendedBadgeFlag,
    updateLDContext,
  ])

  return data
}
